/* Brief Jun 11 — Système de couleurs unifié des événements calendrier + légende.
 *
 * | Type d'événement                        | Couleur     | Hex      |
 * |-----------------------------------------|-------------|----------|
 * | Session que j'ai créée (expert)         | Violet      | #7F77DD  |
 * | Session réservée par moi (apprenant)    | Teal        | #1D9E75  |
 * | Event public                            | Ambre       | #BA7517  |
 * | Série récurrente                        | Bleu        | #378ADD  |
 * | Invitation 1-to-1 en attente            | Rose/Coral  | #D4537E  |
 */
import React from 'react';

export const EVENT_COLORS = {
  mine:        { base: '#7F77DD', textDark: '#C9C5F2' },
  booked:      { base: '#1D9E75', textDark: '#7DDBB8' },
  // Brief Jul 7 2026 §3 — légende simplifiée à 4 catégories.
  pending:      { base: '#BA7517', textDark: '#E8B36A' },
  availability: { base: '#9FE1CB', textDark: '#C8F0E2' },
  // Conservés pour le calendrier PUBLIC (PUBLIC_LEGEND / variant 'auto').
  publicEvent: { base: '#BA7517', textDark: '#E8B36A' },
  series:      { base: '#378ADD', textDark: '#8FC2F0' },
  invitation:  { base: '#D4537E', textDark: '#F0A1BC' },
  bookedPending: { base: '#9CA3AF', textDark: '#D1D5DB' },
  // Brief LOT5 — sessions privées anonymisées sur le calendrier public.
  busy:        { base: '#6B7280', textDark: '#B8BEC9' },
};

export const hexToRgba = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** Brief Jul 7 2026 §3 — mapping PRIVÉ (Mes sessions), 4 catégories :
 *  violet = créées par moi · teal = réservées · ambre = en attente · vert clair = dispos. */
export const colorKeyFor = (resource) => {
  if (!resource) return 'mine';
  if (resource._kind === 'booked_by_me') {
    if (resource.status === 'pending' || resource.status === 'pending_payment') return 'pending';
    return 'booked';
  }
  if (resource.type === 'private_1on1' && resource.invitation_status === 'pending') return 'pending';
  if (resource.type === 'public_slot') return 'availability';
  return 'mine';
};

/** Mapping PUBLIC (profil expert) — inchangé (ambre events, bleu séries). */
export const colorKeyForPublic = (resource) => {
  if (!resource) return 'mine';
  if (resource.type === 'busy') return 'busy';
  if (resource.type === 'public_event') return 'publicEvent';
  if (resource.type === 'public_series') return 'series';
  return 'mine';
};

/** Style object for react-big-calendar eventPropGetter.
 *  variant 'dark'  → calendrier privé (fond #0d1f35) : texte clair
 *  variant 'auto'  → calendrier public (fond clair OU sombre) : texte = hex de base */
export const eventStyleFor = (resource, variant = 'dark') => {
  const key = variant === 'auto' ? colorKeyForPublic(resource) : colorKeyFor(resource);
  const c = EVENT_COLORS[key];
  return {
    style: {
      background: hexToRgba(c.base, 0.16),
      border: `1px solid ${hexToRgba(c.base, 0.6)}`,
      color: variant === 'dark' ? c.textDark : c.base,
      borderRadius: 8,
      padding: '2px 6px',
      fontSize: 12,
      lineHeight: '14px',
      backdropFilter: 'blur(4px)',
      // Brief B5 P1 §9 — hauteur minimale 32px + overflow hidden + ellipsis
      // pour rester lisible quand plusieurs créneaux se superposent dans la
      // vue Semaine. Le titre est rogné automatiquement.
      minHeight: 32,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
};

/* ── Légendes ──────────────────────────────────────────────────────────── */

// Brief Jul 7 2026 §3 — 4 catégories uniquement (events publics + séries font
// partie de "Mes sessions" ; invitations font partie de "En attente").
export const EVENT_LEGEND = [
  { key: 'mine', label: 'Mes sessions', color: EVENT_COLORS.mine.base },
  { key: 'booked', label: 'Sessions réservées', color: EVENT_COLORS.booked.base },
  { key: 'pending', label: 'En attente', color: EVENT_COLORS.pending.base },
  { key: 'availability', label: 'Disponibilités', color: EVENT_COLORS.availability.base },
];

// Version simplifiée pour le calendrier du profil public : seulement les types publics.
export const PUBLIC_LEGEND = [
  { key: 'slot', label: 'Créneaux réservables', color: EVENT_COLORS.mine.base },
  { key: 'public-event', label: 'Events publics', color: EVENT_COLORS.publicEvent.base },
  { key: 'series', label: 'Séries', color: EVENT_COLORS.series.base },
  // Brief LOT5 — blocs anonymisés des sessions privées de l'expert.
  { key: 'busy', label: 'Occupé', color: EVENT_COLORS.busy.base },
];

export const CalendarLegend = ({ items }) => (
  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="calendar-legend">
    {items.map(({ key, label, color }) => (
      <span
        key={key}
        data-testid={`legend-${key}`}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium"
        style={{
          background: hexToRgba(color, 0.12),
          border: `1px solid ${hexToRgba(color, 0.4)}`,
          color,
        }}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        {label}
      </span>
    ))}
  </div>
);

/* ── Toolbar custom react-big-calendar : sélecteur de vue + légende dessous ─ */

const VIEW_LABELS = {
  month: 'Mois', week: 'Semaine', work_week: 'Semaine ouvrée', day: 'Jour', agenda: 'Agenda',
};

/** Factory : retourne un composant toolbar rbc avec la légende rendue SOUS le
 *  sélecteur Mois/Semaine/Jour/Agenda (brief Jun 11). Réutilise les classes
 *  .rbc-toolbar existantes pour hériter du thème dark/light déjà en place. */
export const makeCalendarToolbar = (legendItems) => {
  const CalendarToolbar = ({ label, onNavigate, onView, view, views }) => {
    const viewList = Array.isArray(views) ? views : Object.keys(views || {});
    return (
      <div data-testid="calendar-toolbar">
        <div className="rbc-toolbar">
          <span className="rbc-btn-group">
            <button type="button" onClick={() => onNavigate('TODAY')} data-testid="cal-nav-today">Aujourd'hui</button>
            <button type="button" onClick={() => onNavigate('PREV')} data-testid="cal-nav-prev">←</button>
            <button type="button" onClick={() => onNavigate('NEXT')} data-testid="cal-nav-next">→</button>
          </span>
          <span className="rbc-toolbar-label">{label}</span>
          <span className="rbc-btn-group">
            {viewList.map((v) => (
              <button
                key={v}
                type="button"
                className={view === v ? 'rbc-active' : ''}
                onClick={() => onView(v)}
                data-testid={`cal-view-${v}`}
              >
                {VIEW_LABELS[v] || v}
              </button>
            ))}
          </span>
        </div>
        <CalendarLegend items={legendItems} />
      </div>
    );
  };
  return CalendarToolbar;
};
